import React, { useState, useContext } from "react";
import i18n from "~/assets/js/Helpers/i18n";
import { formatPrice } from "~/assets/js/Format";
import Button from "~/assets/js/components/Button";
import { CheckoutContext } from "~/assets/js/pages/Checkout/CheckoutProvider";
import { AddToast } from "~/assets/js/components/Toast";
import CategoryItems from "~/assets/js/components/CategoryItems";
import { isEmpty } from "lodash";
import { ProductCardProps } from "../type";
import SeeIngredientsCTA from "../SeeIngredientsCTA";
import IngredientsOverlay from "../IngredientsOverlay";
import { useSpring, animated, config } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { useCartContext } from "~/assets/js/global/cartContext";
import { getLocalPrice } from "~/assets/js/Helpers/util";

const SliderProductCard = ({ product, storeId = 0 }) => {
    const { actions, cartLoading, cartError } = useCartContext();
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [refCard, { height: cardHeight }] = useMeasure();
    const [refImage, { height: imageHeight }] = useMeasure();
    const imageExpandAnimation = useSpring({
        maxHeight: overlayOpen ? cardHeight : imageHeight,
        objectFit: overlayOpen ? "cover" : "contain",
        config: { ...config.gentle, duration: 300 },
    });
    const hasIngredients = product["ingredients"] !== undefined;

    const wasPrice =
        product.was_prices?.find((set) => set.store == storeId) ??
        product.was_prices?.find((set) => set.store == 0);

    const displayPrice = getLocalPrice(storeId, product);

    const envDetails = {
        energy: product.energy,
        weight: product.weight,
        co2: product.co2,
    };

    const showOverlay = () => setOverlayOpen(true);
    const hideOverlay = () => setOverlayOpen(false);

    const checkoutContext = useContext(CheckoutContext);

    const isOnCheckout =
        checkoutContext !== undefined && !isEmpty(checkoutContext);

    const handleClick = () => {
        isOnCheckout ? addItemToCart(product.product_id) : seeProduct(); // TODO Get Dashboard ID
    };

    // Logic for checkout slider product carts
    const addItemToCart = async (key) => {
        const { dispatch } = checkoutContext;
        await actions.addItemToCart(key);

        if (!cartError) {
            return dispatch(
                AddToast(
                    "success",
                    i18n.t("toasts.add_product.title_success", {
                        name: product.name,
                    })
                )
            );
        }

        const errorTitle = i18n.t("toasts.add_product.title_error", {
            name: product.name,
        });

        let msg = response?.msg || ""; //TODO HANDLE ERROR MESSAGE

        return dispatch(
            AddToast("danger", errorTitle, msg, () => addItemToCart(key))
        );
    };

    const seeProduct = () => (location.href = product.url);
    const tags = product.attributes.map((attr) => attr.slug);

    const CTA_label = isOnCheckout
        ? "complementary.addToCart"
        : "complementary.seeProduct";

    return (
        <div className="slider-product-card" ref={refCard}>
            <>
                <IngredientsOverlay
                    productName={product.name}
                    open={overlayOpen}
                    ingredients={product.ingredients}
                    onClose={hideOverlay}
                    customClass="ingredients-overlay--slider"
                    envDetails={envDetails}
                    url={product.url}
                />
                {overlayOpen && (
                    <animated.img
                        style={imageExpandAnimation}
                        className="slider-product-card__image--overlay"
                        src={product.image.thumbnail}
                        srcSet={product.image.srcset}
                        alt={`${product.name} - ${product.image.alt}`}
                    />
                )}
            </>
            <div className="slider-product-card__image">
                <a href={product.url}>
                    <img
                        ref={refImage}
                        src={product.image.thumbnail}
                        srcSet={product.image.srcset}
                        alt={product.name}
                    />
                </a>
                {hasIngredients && <SeeIngredientsCTA onClick={showOverlay} />}
            </div>
            <div className="slider-product-card__content">
                <div>
                    <h4 className="slider-product-card__title">
                        {product.name}
                    </h4>
                    <h5 className="slider-product-card__price">
                        {wasPrice &&
                            parseFloat(wasPrice.price) > displayPrice && (
                                <s>{formatPrice(wasPrice.price)}</s>
                            )}
                        {formatPrice(displayPrice)}
                    </h5>
                </div>
                <div className="slider-product-card__content__lower">
                    <CategoryItems tags={tags} />
                    <Button
                        customStyle="slider-product-card__CTA"
                        onClick={() => handleClick(product.id)}
                        label={i18n.t(CTA_label)}
                        loading={cartLoading}
                        variant="primary"
                        size="SM"
                    />
                </div>
            </div>
        </div>
    );
};

SliderProductCard.propTypes = ProductCardProps;

export default SliderProductCard;
